import { type QueryClient, type QueryKey, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useMemo } from 'react'
import { useDirectoryUsers } from '@/features/access/api'
import { useAuth } from '@/lib/auth/AuthProvider'
import { api, request, type Query } from '@/lib/api/client'
import { ApiError, isApiError } from '@/lib/api/errors'
import { asList } from '@/lib/api/list'
import {
  asRow,
  emptyNames,
  followupChannel,
  installCode,
  isoAt,
  mapCollection,
  mapCustomer,
  mapEmail,
  mapFile,
  mapFollowup,
  mapInvoice,
  mapMeeting,
  mapModule,
  mapOffer,
  mapOrder,
  mapReceipt,
  mapRenewal,
  mapRequest,
  mapSummary,
  mapTimeline,
  mapTrial,
  mapWork,
  meetingKind,
  money,
  offerSummaryOf,
  overviewFrom,
  renewalKindToApi,
  renewalStatusToApi,
  requestStatusToApi,
  salesCode,
  stamp,
  str,
  type Names,
  type Row,
  type StageRow,
  unpack,
} from './map'
import type {
  CollectionInput,
  CustomerInput,
  EmailInput,
  FileInput,
  FollowupInput,
  InvoiceInput,
  License,
  MeetingInput,
  ModuleInput,
  OfferInput,
  OfferStatus,
  Priority,
  ReceiptInput,
  RenewalInput,
  RequestInput,
  Stage,
  TrialInput,
  WorkInput,
} from './types'

const root = ['crm'] as const
/** Hesaba daraltılabilen listeler: `customerId` verilirse yalnız o müşterinin kayıtları istenir. */
const scoped = (name: string) => (customerId?: string) => ['crm', name, customerId || 'all'] as const

export const crmKeys = {
  root,
  names: ['crm', 'names'] as const,
  lookups: ['crm', 'lookups'] as const,
  customers: ['crm', 'customers'] as const,
  customer: (id: string) => ['crm', 'customer', id] as const,
  timeline: (id: string) => ['crm', 'timeline', id] as const,
  dashboard: ['crm', 'dashboard'] as const,
  meetings: scoped('meetings'),
  offers: scoped('offers'),
  modules: ['crm', 'modules'] as const,
  orders: scoped('orders'),
  renewals: ['crm', 'renewals'] as const,
  followups: ['crm', 'followups'] as const,
  trials: ['crm', 'trials'] as const,
  work: scoped('work'),
  collections: scoped('collections'),
  invoices: scoped('invoices'),
  receipts: scoped('receipts'),
  requests: scoped('requests'),
  messages: (ticketId: string) => ['crm', 'messages', ticketId] as const,
  emails: scoped('emails'),
  files: scoped('files'),
}

/** Aile önekleri: geçersiz kılma tüm müşteri varyantlarını kapsar. */
const fam = (name: string) => ['crm', name] as const

export function useActor() {
  const { user } = useAuth()
  return [user?.first_name, user?.last_name].filter(Boolean).join(' ') || user?.email || '—'
}

/** Mutasyon sonrası yalnız etkilenen sorgu aileleri yenilenir. */
function useMut<A, R = unknown>(fn: (arg: A) => Promise<R>, affects: readonly QueryKey[]) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: fn,
    onSuccess: () => Promise.all(affects.map((queryKey) => qc.invalidateQueries({ queryKey }))),
  })
}

async function rows(path: string, query?: Query): Promise<Row[]> {
  return asList(await api.get<unknown[] | { items?: unknown[] }>(path, query)).map(asRow)
}

function idOf(data: unknown, fallback = ''): string {
  return str(asRow(data).id) || fallback
}

const accountQuery = (customerId?: string): Query | undefined => (customerId ? { account_id: customerId } : undefined)

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i

function resolveId(byName: Map<string, string>, value: string): string {
  const v = value.trim()
  if (!v) return ''
  if (UUID.test(v)) return v
  return byName.get(v.toLocaleLowerCase('tr')) ?? ''
}

async function loadNames(): Promise<Names> {
  const names = emptyNames()
  const [sectors, regions, stages] = await Promise.all([
    rows('/api/v1/catalog/sectors').catch(() => [] as Row[]),
    rows('/api/v1/regions').catch(() => [] as Row[]),
    api.get<{ sales?: { stages?: StageRow[] }; install?: { stages?: StageRow[] } }>('/api/v1/pipeline-stages').catch(() => null),
  ])
  for (const s of sectors) {
    const id = str(s.id)
    const name = str(s.name)
    if (id && name) names.sectorName.set(id, name)
  }
  for (const r of regions) {
    const id = str(r.id)
    const name = str(r.name)
    if (id && name) {
      names.regionName.set(id, name)
      names.regionId.set(name.toLocaleLowerCase('tr'), id)
    }
  }
  names.sales = stages?.sales?.stages ?? []
  names.install = stages?.install?.stages ?? []
  return names
}

/** Sektör, bölge ve aşama sözlükleri tek önbellekten (5 dk) okunur; her liste isteğinde tekrar çekilmez. */
const namesOf = (qc: QueryClient) => qc.ensureQueryData({ queryKey: crmKeys.names, queryFn: loadNames, staleTime: 5 * 60_000 })

const desc = <T>(key: (x: T) => unknown) => (a: T, b: T) => stamp(key(b)) - stamp(key(a))

// --- Okuma ---

export function useCrmLookups() {
  const users = useDirectoryUsers()
  const catalog = useQuery({
    queryKey: crmKeys.lookups,
    staleTime: 5 * 60_000,
    queryFn: async () => {
      const [projects, modules] = await Promise.all([rows('/api/v1/projects').catch(() => [] as Row[]), rows('/api/v1/catalog/modules').catch(() => [] as Row[])])
      const seen = new Set<string>()
      const projectList = projects.flatMap((p) => {
        const id = str(p.product_id) || str(p.id)
        if (!id || seen.has(id)) return []
        seen.add(id)
        return [{ id, name: str(p.name) || str(p.code) || id }]
      })
      return { projects: projectList, moduleCatalog: modules.map((m) => str(m.name)).filter(Boolean) }
    },
  })
  const data = useMemo(
    () =>
      catalog.data || users.data
        ? {
            staff: (users.data ?? []).filter((u) => u.isActive).map((u) => ({ id: u.id, name: u.name, title: u.position || u.roleNames[0] || '' })),
            projects: catalog.data?.projects ?? [],
            moduleCatalog: catalog.data?.moduleCatalog ?? [],
          }
        : undefined,
    [catalog.data, users.data],
  )
  return { ...catalog, data }
}

export function useCustomers() {
  const qc = useQueryClient()
  return useQuery({
    queryKey: crmKeys.customers,
    queryFn: async () => {
      const [accounts, names] = await Promise.all([rows('/api/v1/accounts'), namesOf(qc)])
      return accounts.map((a) => mapCustomer(a, names))
    },
  })
}

export function useCustomer(id: string) {
  const qc = useQueryClient()
  return useQuery({
    queryKey: crmKeys.customer(id),
    enabled: !!id,
    queryFn: async () => {
      const [hub, names] = await Promise.all([api.get<Row>(`/api/v1/accounts/${id}/hub`), namesOf(qc)])
      const account = { ...asRow(hub.account), id }
      const finance = hub.finance ? asRow(hub.finance) : null
      const accepted = money(hub.accepted_quotes_total)
      const customer = mapCustomer(account, names, {
        contact: hub.primary_contact ? asRow(hub.primary_contact) : null,
        links: Array.isArray(hub.links) ? hub.links.map(asRow) : [],
        team: Array.isArray(hub.team) ? hub.team.map(asRow) : [],
        followup: hub.next_action ? asRow(hub.next_action) : null,
        lastMeeting: str(hub.last_meeting_on) || null,
        balance: finance?.pending_collection_total,
      })
      return { customer, summary: mapSummary(finance, Number(hub.open_tickets_count) || 0, accepted !== '0.00' ? accepted : undefined) }
    },
  })
}

export const useTimeline = (id: string) =>
  useQuery({
    queryKey: crmKeys.timeline(id),
    queryFn: async () => {
      const items = await rows(`/api/v1/accounts/${id}/timeline`, { limit: 100 })
      return items.map(mapTimeline).filter((x): x is NonNullable<typeof x> => !!x)
    },
    enabled: !!id,
  })

export const useFollowups = () => useQuery({ queryKey: crmKeys.followups, queryFn: async () => (await rows('/api/v1/followups', { status: 'all' })).map(mapFollowup) })

/** Genel bakış: müşteri listesi, takipler ve pano aynı önbellekleri paylaşır (ek liste isteği yok). */
export function useCrmOverview(projectId: string) {
  const customers = useCustomers()
  const followups = useFollowups()
  const dash = useQuery({ queryKey: crmKeys.dashboard, queryFn: () => api.get<Row>('/api/v1/accounts/dashboard').catch(() => null) })
  const data = useMemo(() => {
    if (!customers.data) return undefined
    const scopedList = customers.data.filter((c) => projectId === 'all' || c.projectId === projectId)
    return overviewFrom(scopedList, dash.data?.pending_collection_total, followups.data ?? [])
  }, [customers.data, followups.data, dash.data, projectId])
  return { data, isLoading: customers.isLoading, isError: customers.isError }
}

export const useMeetings = (customerId?: string) =>
  useQuery({
    queryKey: crmKeys.meetings(customerId),
    queryFn: async () =>
      (await rows('/api/v1/meetings', { include: 'details', ...accountQuery(customerId) })).map((m) => mapMeeting(m)).sort((a, b) => `${b.date}${b.start}`.localeCompare(`${a.date}${a.start}`)),
  })

export const useOffers = (customerId?: string) =>
  useQuery({
    queryKey: crmKeys.offers(customerId),
    queryFn: async () => (await rows('/api/v1/quotes', { include: 'lines', ...accountQuery(customerId) })).map((q) => mapOffer(q, null)).sort((a, b) => b.date.localeCompare(a.date)),
  })

export const useOrders = (customerId?: string) =>
  useQuery({
    queryKey: crmKeys.orders(customerId),
    queryFn: async () => (await rows('/api/v1/orders', accountQuery(customerId))).map(mapOrder).sort(desc((o) => o.createdAt)),
  })

export const useCustomerModules = () =>
  useQuery({
    queryKey: crmKeys.modules,
    queryFn: async () =>
      (await rows('/api/v1/entitlements')).flatMap((ent) => {
        const items = Array.isArray(ent.items) ? ent.items.map(asRow) : []
        return items.map((item) => mapModule(str(ent.account_id), str(ent.id), item, ent.license_term))
      }),
  })

export const useWork = (customerId?: string) =>
  useQuery({ queryKey: crmKeys.work(customerId), queryFn: async () => (await rows('/api/v1/crm/deployments', accountQuery(customerId))).map(mapWork).sort((a, b) => b.date.localeCompare(a.date)) })

export const useCollections = (customerId?: string) =>
  useQuery({
    queryKey: crmKeys.collections(customerId),
    queryFn: async () => (await rows('/api/v1/crm/payment-plans', accountQuery(customerId))).map(mapCollection).sort((a, b) => a.dueDate.localeCompare(b.dueDate)),
  })

export const useInvoices = (customerId?: string) =>
  useQuery({ queryKey: crmKeys.invoices(customerId), queryFn: async () => (await rows('/api/v1/crm/invoices', accountQuery(customerId))).map(mapInvoice).sort((a, b) => b.date.localeCompare(a.date)) })

export const useReceipts = (customerId?: string) =>
  useQuery({ queryKey: crmKeys.receipts(customerId), queryFn: async () => (await rows('/api/v1/crm/receipts', accountQuery(customerId))).map(mapReceipt).sort((a, b) => b.date.localeCompare(a.date)) })

export const useEmails = (customerId?: string) =>
  useQuery({ queryKey: crmKeys.emails(customerId), queryFn: async () => (await rows('/api/v1/crm/emails', accountQuery(customerId))).map(mapEmail).sort(desc((e) => e.sentAt)) })

export const useFiles = (customerId?: string) =>
  useQuery({ queryKey: crmKeys.files(customerId), queryFn: async () => (await rows('/api/v1/crm/files', accountQuery(customerId))).map(mapFile).sort(desc((f) => f.uploadedAt)) })

/** Talepler listesi mesajları içermez; yazışma talep açıldığında ayrı yüklenir. */
export const useRequests = (customerId?: string) =>
  useQuery({ queryKey: crmKeys.requests(customerId), queryFn: async () => (await rows('/api/v1/tickets', accountQuery(customerId))).map((t) => mapRequest(t)).sort(desc((r) => r.createdAt)) })

export const useRequestMessages = (ticketId: string) =>
  useQuery({
    queryKey: crmKeys.messages(ticketId),
    enabled: !!ticketId,
    queryFn: async () =>
      (await rows('/api/v1/messages', { ticket_id: ticketId }))
        .map((m) => ({ id: str(m.id), author: str(m.author_user_id), body: str(m.body), at: str(m.created_at) }))
        .sort((a, b) => stamp(a.at) - stamp(b.at)),
  })

export const useRenewals = () => useQuery({ queryKey: crmKeys.renewals, queryFn: async () => (await rows('/api/v1/renewals')).map(mapRenewal) })
export const useTrials = () => useQuery({ queryKey: crmKeys.trials, queryFn: async () => (await rows('/api/v1/demos')).map(mapTrial) })

/** Teklif özeti teklif ve müşteri önbelleklerinden türetilir. */
export function useOfferSummary(scope: { customerId?: string; projectId?: string }) {
  const offers = useOffers(scope.customerId)
  const customers = useCustomers()
  const data = useMemo(() => {
    if (!offers.data) return undefined
    const projectOf = new Map((customers.data ?? []).map((c) => [c.id, c.projectId]))
    const inProject = (customerId: string) => !scope.projectId || scope.projectId === 'all' || projectOf.get(customerId) === scope.projectId
    return offerSummaryOf(offers.data.filter((o) => inProject(o.customerId)))
  }, [offers.data, customers.data, scope.projectId])
  return { data, isLoading: offers.isLoading }
}

// --- Yazma ---

const CUSTOMER = [crmKeys.customers, fam('customer'), crmKeys.dashboard] as const

async function accountBody(input: CustomerInput, names: Names, projects: { id: string; productId: string }[]) {
  const project = projects.find((p) => p.productId === input.projectId || p.id === input.projectId)
  const body: Record<string, string> = {
    title: input.name.trim(),
    product_id: project?.productId || input.projectId,
    // Form ürün sektörünün kimliğini taşır (ad eşlemesi yok).
    sector_id: input.sector.trim(),
    tax_id: input.taxNo,
    work_email: input.email,
    sales_stage: salesCode(names.sales, input.stage),
    install_stage: installCode(names.install, input.delivery),
    legal_name: input.legalName,
    tax_office: input.taxOffice,
    city: input.city,
    district: input.district,
    address: input.address,
  }
  const region = resolveId(names.regionId, input.region)
  if (region) body.region_id = region
  if (project?.id) body.project_id = project.id
  if (input.stage === 'lost') body.lost_reason_code = 'diger'
  return body
}

async function saveLinks(accountId: string, input: CustomerInput) {
  const links = [
    input.website ? { link_type: 'website', url: input.website } : null,
    input.appUrl ? { link_type: 'app', url: input.appUrl } : null,
    input.adminUrl ? { link_type: 'admin', url: input.adminUrl } : null,
  ].filter((x): x is { link_type: string; url: string } => !!x)
  if (links.length) await api.put(`/api/v1/accounts/${accountId}/links`, { links })
}

async function saveContact(accountId: string, input: CustomerInput) {
  if (input.contactName.trim().length < 2) return
  const list = await rows('/api/v1/contacts', { account_id: accountId })
  const primary = list.find((c) => c.is_primary) ?? list[0]
  const body = { full_name: input.contactName.trim(), title: input.contactTitle, email: input.email, phone: input.phone, is_primary: true }
  if (primary) await api.put(`/api/v1/contacts/${str(primary.id)}`, body)
  else await api.post('/api/v1/contacts', { account_id: accountId, ...body })
}

async function saveTeam(accountId: string, input: CustomerInput) {
  const members = input.team.filter((m) => m.userId)
  await Promise.all(members.map((m) => api.post(`/api/v1/accounts/${accountId}/team`, { user_id: m.userId, role_label: m.role })))
}

export function useSaveCustomer() {
  const qc = useQueryClient()
  return useMut(async ({ id, input }: { id?: string; input: CustomerInput }) => {
    const [names, projects] = await Promise.all([
      namesOf(qc),
      rows('/api/v1/projects').then((list) => list.map((p) => ({ id: str(p.id), productId: str(p.product_id) }))),
    ])
    const body = await accountBody(input, names, projects)
    const saved = id ?? idOf(await api.post('/api/v1/accounts', body))
    if (id) await api.put(`/api/v1/accounts/${id}`, body)
    await Promise.all([saveContact(saved, input), saveLinks(saved, input), saveTeam(saved, input)])
    return saved
  }, CUSTOMER)
}

export function useSetStage() {
  const qc = useQueryClient()
  return useMut(async ({ id, stage }: { id: string; stage: Stage }) => {
    const names = await namesOf(qc)
    const body: Record<string, string> = { sales_stage: salesCode(names.sales, stage) }
    if (stage === 'lost') body.lost_reason_code = 'diger'
    await api.put(`/api/v1/accounts/${id}`, body)
  }, CUSTOMER)
}

export const useDeleteCustomer = () => useMut((id: string) => api.del(`/api/v1/accounts/${id}`), CUSTOMER)

// Toplantılar

const MEETING = [fam('meetings'), crmKeys.customers, fam('customer'), fam('timeline')] as const

/** Önbellekteki toplantı (düzenlemede mevcut sonraki adımı tekrar eklememek için). */
function cachedMeeting(qc: QueryClient, id: string) {
  for (const [, list] of qc.getQueriesData<ReturnType<typeof mapMeeting>[]>({ queryKey: fam('meetings') })) {
    const hit = list?.find((m) => m.id === id)
    if (hit) return hit
  }
  return null
}

export function useSaveMeeting() {
  const qc = useQueryClient()
  return useMut(async ({ id, input }: { id?: string; input: MeetingInput }) => {
    const planned = isoAt(input.date, input.start || '09:00')
    const next = input.nextAction ? { title: input.nextAction.text, owner_user_id: input.nextAction.staffId, due_at: isoAt(input.nextAction.date, '09:00'), is_done: false } : null
    if (!id) {
      const created = idOf(
        await api.post('/api/v1/meetings', {
          account_id: input.customerId,
          kind: meetingKind(input.channel),
          subject: input.summary,
          planned_at: planned,
          ends_at: input.end ? isoAt(input.date, input.end) : undefined,
          staff_user_ids: input.staffIds.length ? input.staffIds : input.staffId ? [input.staffId] : [],
          contact_ids: input.contactIds,
          prep_notes: input.summary,
          visibility: input.visibility,
          actions: next ? [next] : [],
        }),
      )
      // Sonuç, müşteri cevabı ve kararlar oluşturma uç noktasında yok; aynı kayda hemen işlenir.
      if (input.customerResponse || input.decisions || input.result !== 'reached') {
        await api.put(`/api/v1/meetings/${created}`, {
          status: input.result === 'not_reached' ? 'ulasilamadi' : input.result === 'call_again' ? 'planlandi' : 'tamamlandi',
          customer_response: input.customerResponse,
          decisions: input.decisions,
        })
      }
      return created
    }
    await api.put(`/api/v1/meetings/${id}`, {
      status: input.result === 'not_reached' ? 'ulasilamadi' : input.result === 'call_again' ? 'planlandi' : 'tamamlandi',
      subject: input.summary,
      kind: meetingKind(input.channel),
      planned_at: planned,
      ends_at: input.end ? isoAt(input.date, input.end) : '',
      staff_user_ids: input.staffIds.length ? input.staffIds : input.staffId ? [input.staffId] : [],
      contact_ids: input.contactIds,
      customer_response: input.customerResponse,
      decisions: input.decisions,
      visibility: input.visibility,
    })
    const prev = cachedMeeting(qc, id)?.nextAction
    const unchanged = prev && input.nextAction && prev.text === input.nextAction.text && prev.date === input.nextAction.date && prev.staffId === input.nextAction.staffId
    if (next && !unchanged) await api.post(`/api/v1/meetings/${id}/actions`, next)
    return id
  }, [...MEETING, crmKeys.followups])
}

export const useDeleteMeeting = () => useMut((id: string) => api.del(`/api/v1/meetings/${id}`), MEETING)

export const useStartMeeting = () =>
  useMut(
    ({ customerId, staffId, summary }: { customerId: string; staffId: string; summary: string }) =>
      api
        .post('/api/v1/meetings', {
          account_id: customerId,
          kind: 'fiziki_ziyaret',
          subject: summary,
          planned_at: new Date().toISOString(),
          staff_user_ids: staffId ? [staffId] : [],
          prep_notes: summary,
        })
        .then((r) => idOf(r)),
    MEETING,
  )

export const useFinishMeeting = () => useMut((id: string) => api.put(`/api/v1/meetings/${id}`, { status: 'tamamlandi' }), MEETING)

export const useAddMinute = () =>
  useMut(({ meetingId, text, priority }: { meetingId: string; text: string; priority: Priority }) => api.post(`/api/v1/meetings/${meetingId}/actions`, { title: text.trim(), priority }), [fam('meetings')])

export const useUpdateMinute = () =>
  useMut(
    ({ meetingId, itemId, patch }: { meetingId: string; itemId: string; patch: { text?: string; done?: boolean; priority?: Priority } }) =>
      api.patch(`/api/v1/meetings/${meetingId}/actions/${itemId}`, { title: patch.text, is_done: patch.done, priority: patch.priority }),
    [fam('meetings')],
  )

export const useDeleteMinute = () => useMut(({ meetingId, itemId }: { meetingId: string; itemId: string }) => api.del(`/api/v1/meetings/${meetingId}/actions/${itemId}`), [fam('meetings')])

export const useAddNote = () => useMut(({ meetingId, body }: { meetingId: string; body: string }) => api.put(`/api/v1/meetings/${meetingId}`, { minutes: body }), [fam('meetings')])

// Teklif ve siparişler

const OFFER = [fam('offers'), fam('customer'), crmKeys.customers, fam('timeline')] as const

function quoteBody(input: OfferInput) {
  return {
    account_id: input.customerId,
    quote_date: input.date,
    valid_until: input.validUntil,
    discount_percent: Number(input.discountPct).toFixed(2),
    notes: input.note,
    lines: input.items.map((item) => ({ line_type: 'deliverable', ref_code: item.name, unit_price: item.unitPrice, discount: '0', qty: item.qty })),
  }
}

async function applyOfferStatus(id: string, status: OfferStatus) {
  if (status === 'sent') {
    try {
      await api.post(`/api/v1/quotes/${id}/finalize`, {})
    } catch (e) {
      if (!(isApiError(e) && e.code === 'QUOTE_NOT_DRAFT')) throw e
    }
    return
  }
  if (status === 'accepted') await api.post(`/api/v1/quotes/${id}/accept`, {})
  else if (status === 'rejected') await api.post(`/api/v1/quotes/${id}/reject`, {})
}

/** Teklifi hazırlayanın bu müşteri için azami iskontosu (fiyat düşürme ve kalem iskontosu dahil). */
export const useMyDiscountLimit = (customerId: string) =>
  useQuery({
    queryKey: ['sales', 'my-discount-limit', customerId],
    enabled: !!customerId,
    staleTime: 60_000,
    queryFn: async () => {
      const row = await api.get<Record<string, unknown>>('/api/v1/sales/my-discount-limit', { account_id: customerId })
      return { maxPercent: row.max_percent == null ? null : Number(row.max_percent), override: row.override === true }
    },
  })

export const useSaveOffer = () =>
  useMut(async ({ id, input }: { id?: string; input: OfferInput }) => {
    const saved = id ?? idOf(await api.post('/api/v1/quotes', quoteBody(input)))
    if (id) await api.put(`/api/v1/quotes/${id}`, quoteBody(input))
    await applyOfferStatus(saved, input.status)
    return saved
  }, OFFER)

export const useSetOfferStatus = () => useMut(({ id, status }: { id: string; status: OfferStatus }) => applyOfferStatus(id, status), OFFER)
export const useDeleteOffer = () => useMut((id: string) => api.del(`/api/v1/quotes/${id}`), OFFER)

const ORDER = [fam('orders'), fam('offers'), crmKeys.modules, crmKeys.renewals, fam('customer'), fam('timeline')] as const

export const useConvertOffer = () =>
  useMut(async (offerId: string) => {
    const quote = asRow(await api.get(`/api/v1/quotes/${offerId}`))
    await api.post(`/api/v1/quotes/${offerId}/accept`, {})
    const lines = Array.isArray(quote.lines) ? quote.lines.map(asRow) : []
    return idOf(
      await api.post('/api/v1/orders', {
        account_id: str(quote.account_id),
        quote_id: offerId,
        lines: lines.map((l) => ({ line_type: str(l.line_type) || 'deliverable', ref_code: str(l.ref_code), unit_price: money(l.unit_price), discount: money(l.discount), qty: Number(l.qty) || 1 })),
      }),
    )
  }, [...ORDER, crmKeys.customers])

export const useApproveOrder = () =>
  useMut(
    ({ id, license }: { id: string; license: License }) =>
      api.post(`/api/v1/orders/${id}/approve`, {
        branch_limit: license.branchLimit,
        license_term: license.term,
        valid_until: license.validUntil,
        maintenance_until: license.maintenanceUntil,
        support_until: license.supportUntil,
      }),
    ORDER,
  )
export const useRejectOrder = () => useMut(({ id, note }: { id: string; note: string }) => api.post(`/api/v1/orders/${id}/reject`, { note }), ORDER)
export const useDeleteOrder = () => useMut((id: string) => api.del(`/api/v1/orders/${id}`), ORDER)

// Modüller (hak kayıtları)

export const useSaveModule = () =>
  useMut(async ({ id, input }: { id?: string; input: ModuleInput }) => {
    const ents = await rows('/api/v1/entitlements', { account_id: input.customerId })
    const ent = id ? (ents.find((e) => str(e.id) === unpack(id)[0]) ?? ents[0]) : ents[0]
    if (!ent) throw new ApiError(404, 'NOT_FOUND', 'entitlement not found')
    const entId = str(ent.id)
    const until = input.startDate || ''
    if (id) await api.put(`/api/v1/entitlements/${entId}/items/module/${unpack(id)[2] || input.moduleId}`, { valid_until: until })
    else await api.post(`/api/v1/entitlements/${entId}/items`, { kind: 'module', code: input.moduleId, valid_until: until })
  }, [crmKeys.modules])

export const useDeleteModule = () =>
  useMut((id: string) => {
    const [entId, kind, code] = unpack(id)
    return api.del(`/api/v1/entitlements/${entId}/items/${kind || 'module'}/${code || id}`)
  }, [crmKeys.modules])

// Kurulum / eğitim işleri

const WORK = [fam('work'), fam('timeline')] as const

export const useSaveWork = () =>
  useMut(async ({ id, input }: { id?: string; input: WorkInput }) => {
    const [accountId, runId] = id ? unpack(id) : [input.customerId, '']
    const body = { run_type: input.kind, title: input.note || input.kind, notes: input.note, scheduled_at: isoAt(input.date, '09:00'), status: input.status, assignee_user_id: input.staffId }
    if (runId) await api.put(`/api/v1/accounts/${accountId}/deployments/${runId}`, body)
    else await api.post(`/api/v1/accounts/${input.customerId}/deployments`, body)
  }, WORK)

export const useDeleteWork = () =>
  useMut((id: string) => {
    const [accountId, runId] = unpack(id)
    return api.del(`/api/v1/accounts/${accountId}/deployments/${runId}`)
  }, WORK)

// Finans: tahsilat planı, fatura, dekont

const FINANCE = [fam('collections'), fam('invoices'), fam('receipts'), fam('customer'), crmKeys.customers, crmKeys.dashboard] as const

export const useSaveCollection = () =>
  useMut(({ id, input }: { id?: string; input: CollectionInput }) => {
    const body = { title: input.description || 'Tahsilat', amount: Number(input.amount), due_date: input.dueDate, notes: input.description }
    const [accountId, planId] = id ? unpack(id) : [input.customerId, '']
    return planId ? api.put(`/api/v1/accounts/${accountId}/payment-plans/${planId}`, body) : api.post(`/api/v1/accounts/${input.customerId}/payment-plans`, body)
  }, FINANCE)

export const useSaveInvoice = () =>
  useMut((input: InvoiceInput & { file?: File | null }) => {
    const form = new FormData()
    form.set('invoice_no', input.number.trim())
    form.set('invoice_date', input.date)
    form.set('amount', String(Number(input.amount)))
    if (input.collectionId) form.set('payment_plan_id', unpack(input.collectionId).at(-1) ?? '')
    if (input.file) form.set('file', input.file)
    return request('POST', `/api/v1/accounts/${input.customerId}/invoices`, { body: form })
  }, FINANCE)

export const useDeleteInvoice = () =>
  useMut((id: string) => {
    const [accountId, invoiceId] = unpack(id)
    return api.del(`/api/v1/accounts/${accountId}/invoices/${invoiceId}`)
  }, FINANCE)

export const useSaveReceipt = () =>
  useMut((input: ReceiptInput & { file?: File | null }) => {
    const form = new FormData()
    const plan = unpack(input.collectionId).at(-1) ?? ''
    if (plan) form.set('payment_plan_id', plan)
    form.set('amount', String(Number(input.amount)))
    form.set('paid_at', isoAt(input.date, '12:00'))
    form.set('method', input.reference)
    if (input.file) form.set('file', input.file)
    return request('POST', `/api/v1/accounts/${input.customerId}/receipts`, { body: form })
  }, FINANCE)

export const useDeleteReceipt = () =>
  useMut((id: string) => {
    const [accountId, receiptId] = unpack(id)
    return api.del(`/api/v1/accounts/${accountId}/receipts/${receiptId}`)
  }, FINANCE)

/** Fatura/dekont/dosya ekini indirir (yetki başlığıyla; bağlantı açılmaz). */
export function downloadCrmFile(kind: 'invoice' | 'receipt' | 'file', id: string, name: string) {
  const [accountId, recordId] = unpack(id)
  const path = {
    invoice: `/api/v1/accounts/${accountId}/invoices/${recordId}/file`,
    receipt: `/api/v1/accounts/${accountId}/receipts/${recordId}/file`,
    file: `/api/v1/accounts/${accountId}/files/${recordId}/download`,
  }[kind]
  return api.download(path, name || 'dosya')
}

// Talepler

const REQUEST = [fam('requests'), fam('customer'), fam('timeline')] as const

export const useSaveRequest = () =>
  useMut(({ id, input }: { id?: string; input: RequestInput }) => {
    const body = {
      account_id: input.customerId,
      title: input.title,
      description: input.description,
      module_code: input.module,
      priority: input.priority,
      assignee_user_id: input.assigneeId ?? '',
      status: requestStatusToApi(input.status),
    }
    return id ? api.put(`/api/v1/tickets/${id}`, body) : api.post('/api/v1/tickets', body)
  }, REQUEST)

export const useDeleteRequest = () => useMut((id: string) => api.del(`/api/v1/tickets/${id}`), REQUEST)

export function useAddRequestMessage() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ requestId, body }: { requestId: string; body: string }) => api.post('/api/v1/messages', { ticket_id: requestId, body }),
    onSuccess: (_d, { requestId }) => qc.invalidateQueries({ queryKey: crmKeys.messages(requestId) }),
  })
}

// E-posta ve dosyalar

const escapeHtml = (s: string) => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;')

/**
 * Müşteriye kişinin şirket mail hesabından gönderir (imza sunucuda eklenir) ve maili müşteriye bağlar.
 * Gelen yanıtlar kişi/şirket e-posta adresine göre otomatik olarak müşteri geçmişine düşer.
 */
export const useSendEmail = () =>
  useMut((input: EmailInput) => {
    const form = new FormData()
    form.set('account_id', input.customerId)
    form.set('to', input.to)
    form.set('subject', input.subject)
    form.set('body_html', input.body.split(/\n{2,}/).map((p) => `<p>${escapeHtml(p).replace(/\n/g, '<br>')}</p>`).join(''))
    return request('POST', '/api/v1/mail/send', { body: form })
  }, [fam('emails'), fam('timeline')])

function fileForm(input: FileInput & { file?: File | null }) {
  const form = new FormData()
  form.set('category', input.category)
  form.set('title', input.title.trim())
  form.set('description', input.description)
  form.set('visibility', input.visibility)
  form.set('meeting_id', input.meetingId ?? '')
  if (input.file) form.set('file', input.file)
  return form
}

export const useSaveFile = () =>
  useMut(({ id, input }: { id?: string; input: FileInput & { file?: File | null } }) => {
    const [accountId, fileId] = id ? unpack(id) : [input.customerId, '']
    return fileId
      ? request('PUT', `/api/v1/accounts/${accountId}/files/${fileId}`, { body: fileForm(input) })
      : request('POST', `/api/v1/accounts/${input.customerId}/files`, { body: fileForm(input) })
  }, [fam('files')])

export const useUnlinkFile = () =>
  useMut(({ id, reason }: { id: string; reason: string }) => {
    const [accountId, fileId] = unpack(id)
    return request('DELETE', `/api/v1/accounts/${accountId}/files/${fileId}`, { query: { reason } })
  }, [fam('files')])

// Yenileme, takip, deneme

const RENEWAL = [crmKeys.renewals] as const

export const useSaveRenewal = () =>
  useMut(({ id, input }: { id?: string; input: RenewalInput }) => {
    const body = {
      account_id: input.customerId,
      kind: renewalKindToApi(input.kind),
      due_at: input.dueOn,
      status: renewalStatusToApi(input.status),
      notes: input.notes,
      amount: Number(input.amount) || 0,
      order_id: input.orderId ?? '',
    }
    return id ? api.put(`/api/v1/renewals/${id}`, body) : api.post('/api/v1/renewals', body)
  }, RENEWAL)
export const useDeleteRenewal = () => useMut((id: string) => api.del(`/api/v1/renewals/${id}`), RENEWAL)

const FOLLOWUP = [crmKeys.followups, crmKeys.customers, fam('customer')] as const

export const useSaveFollowup = () =>
  useMut(({ id, input }: { id?: string; input: FollowupInput }) => {
    const body = { account_id: input.customerId, channel: followupChannel(input.channel), due_at: isoAt(input.dueOn, '09:00'), owner_user_id: input.ownerId, note: input.note }
    return id ? api.put(`/api/v1/followups/${id}`, body) : api.post('/api/v1/followups', body)
  }, FOLLOWUP)

/** Durum önbellekteki takipten okunur; ekstra liste isteği atılmaz. */
export function useToggleFollowup() {
  const qc = useQueryClient()
  return useMut(async (id: string) => {
    const current = qc.getQueryData<ReturnType<typeof mapFollowup>[]>(crmKeys.followups)?.find((f) => f.id === id)
    await api.put(`/api/v1/followups/${id}`, { done: !current?.done })
  }, FOLLOWUP)
}
export const useDeleteFollowup = () => useMut((id: string) => api.del(`/api/v1/followups/${id}`), FOLLOWUP)

const TRIAL = [crmKeys.trials, fam('orders')] as const

export const useStartTrial = () => useMut((input: TrialInput) => api.post('/api/v1/demos', { account_id: input.customerId, modules: input.modules, days: input.days, notes: input.notes, assignments: input.assignments ?? {} }).then((r) => idOf(r)), TRIAL)
export const useExtendTrial = () => useMut(({ id, days, reason }: { id: string; days: number; reason: string }) => api.post(`/api/v1/demos/${id}/extend`, { days, reason }), TRIAL)
export const useConvertTrial = () => useMut(({ id, orderId }: { id: string; orderId: string }) => api.post(`/api/v1/demos/${id}/convert`, { order_id: orderId }), TRIAL)
export const useCancelTrial = () => useMut((id: string) => api.post(`/api/v1/demos/${id}/cancel`, {}), TRIAL)
